import React, { Component, Suspense } from 'react';
import { Route, RouteComponentProps } from 'react-router-dom';
import { actions as accountActions } from 'reducers/Account';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { actions as integrationsActions } from 'reducers/accountIntegrations';
import { actions as organizationActions } from 'reducers/organizations';
import { actions as projectsActions } from 'reducers/projects';
import { ownerProjectsSelector } from 'selectors/Projects';
import {
  accountOrganizationsSelector,
  accountSelector,
} from 'selectors/Account';
import { Project } from '../../models/Projects';
import { Organization } from '../../models/Organization';
import Rating from 'containers/Rating';
import { retry } from '../../utils/retry';

const LoggedInApp = React.lazy(() => retry(() => import('./LoggedInApp')));

type FetchRouteParams = {
  providerId: string;
  organizationName: string;
};

interface ProjectsFetchProps extends RouteComponentProps<FetchRouteParams> {
  fetchProjects: (providerId: string, organizationName: string) => void;
  projects?: Project[];
}

/**
 * This component just exists to fetch the projects for the organization if a url is being loaded that includes it. The
 * user is not guaranteed to be logged in yet and the providerId/organizationName have not been validated. Main goal
 * of doing this here is it prevents us needing to wait until the account info has been fetched reducing the TTI.
 */
class ProjectsFetchComponent extends Component<ProjectsFetchProps> {
  componentDidMount(): void {
    const { providerId, organizationName } = this.props.match.params;
    if (!this.props.projects?.length) {
      this.props.fetchProjects(providerId, organizationName);
    }
  }
  render() {
    return null;
  }
}

interface AppProps extends RouteComponentProps<FetchRouteParams> {
  // TODO: move this into the account reducers
  account: {
    user: object | null;
  };
  integrations: Array<any>;
  fetchAccount: () => void;
  fetchIntegrations: () => void;
  fetchProjects: (providerId: string, organizationName: string) => void;
  projects?: Project[];
  organizations?: Organization[];
}

class App extends Component<AppProps> {
  constructor(props: AppProps) {
    super(props);
    props.fetchAccount();
    props.fetchIntegrations();
  }
  render() {
    const {
      history,
      location,
      match,
      fetchProjects,
      account,
      integrations,
      projects,
      organizations,
    } = this.props;
    const isLoggedIn = !!account.user && integrations.length > 0;
    return (
      <div>
        {isLoggedIn && (
          <Suspense fallback={null}>
            <LoggedInApp
              organizations={organizations}
              match={match}
              history={history}
              location={location}
              user={account.user}
            />
          </Suspense>
        )}
        <Route
          path={`${match.path}/projects/:providerId([1-9])/:organizationName`}
          render={(props) => {
            return (
              <ProjectsFetchComponent
                {...props}
                projects={projects}
                fetchProjects={fetchProjects}
              />
            );
          }}
        />
        <Route
          path={`${match.path}/ratings/:uuid/:helpful/:ratingType?`}
          render={(props: any): any => {
            // Prevent render since we render this in the LoggedInApp routes
            if (isLoggedIn) {
              return null;
            }
            return <Rating {...props} isLoggedIn={false} />;
          }}
        />
      </div>
    );
  }
}

function mapStateToProps(state: any) {
  const { accountIntegrations } = state;
  const projects = ownerProjectsSelector(state);
  const account = accountSelector(state);
  const organizations = accountOrganizationsSelector(state);
  return {
    account,
    integrations: accountIntegrations.integrations,
    organizations,
    projects,
  };
}

function mapDispatchToProps(dispatch: any) {
  return bindActionCreators(
    {
      ...accountActions,
      ...integrationsActions,
      ...projectsActions,
      ...organizationActions,
    },
    dispatch
  );
}

export default connect(mapStateToProps, mapDispatchToProps)(App);
